import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider, CssBaseline, Box, useMediaQuery } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SchoolSettingsProvider, useSchoolSettings } from './contexts/SchoolSettingsContext';
import { CartProvider, useCart } from './contexts/CartContext';
import { DataProvider, useData } from './contexts/DataContext';
import { TourProvider, type TourHostBridge } from './tour/TourProvider';
import TourLauncher from './tour/TourLauncher';
import TourOverlay from './tour/TourOverlay';
import { mockPassword } from './mock';
import { mockWorkers } from './mock/workers';
import type { TourView } from './tour/steps';
import { LoginPage } from './components/auth/LoginPage';
import { ParentRegistration } from './components/auth/ParentRegistration';
import { CommunityRegistration } from './components/auth/CommunityRegistration';
import { StudentSelector } from './components/auth/StudentSelector';
import { DemoBanner } from './components/DemoBanner';
import { AdminLoginPage } from './components/admin/AdminLoginPage';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AppBar } from './components/layout/AppBar';
import { Footer } from './components/layout/Footer';
import { Dashboard } from './components/dashboard/Dashboard';
import { FeeCatalog } from './components/fees/FeeCatalog';
import { CartPage } from './components/cart/CartPage';
import { CheckoutPage } from './components/cart/CheckoutPage';
import { PublicCartAccess } from './components/cart/PublicCartAccess';
import { CommunityShop } from './components/fees/CommunityShop';
import { EventsPage } from './components/events/EventsPage';
import { PurchasesPage } from './components/purchases/PurchasesPage';
import { AccountSettings } from './components/account/AccountSettings';
import { WorkerLoginPage } from './components/pos/WorkerLoginPage';
import { POSInterface } from './components/pos/POSInterface';
import { Worker } from './types';

const queryClient = new QueryClient();

type Route = 'login' | 'register' | 'register-community' | 'dashboard' | 'catalog' | 'cart' | 'checkout' | 'share' | 'community' | 'events' | 'purchases' | 'account' | 'admin' | 'admin-dashboard' | 'pos' | 'pos-terminal';

const EMMA_ID = '550e8400-e29b-41d4-a716-446655440001';
const LIAM_ID = '550e8400-e29b-41d4-a716-446655440002';

const AppContent: React.FC = () => {
  const { isAuthenticated, user, student, login, logout, selectStudent } = useAuth();
  const { cart, addToCart, removeFromCart } = useCart();
  const { fees } = useData();
  const [currentRoute, setCurrentRoute] = useState<Route>('login');
  const [currentWorker, setCurrentWorker] = useState<Worker | null>(null);

  // The bridge needs to see LATEST state (cart, user, etc.) each time it's
  // called — but ensureView must stay referentially stable so the
  // TourProvider's view-change effect doesn't re-fire every render and
  // seed the cart infinitely. Refs give us both: stable callback, live reads.
  const userRef = useRef(user);
  const studentRef = useRef(student);
  const cartRef = useRef(cart);
  const currentWorkerRef = useRef(currentWorker);
  const feesRef = useRef(fees);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { studentRef.current = student; }, [student]);
  useEffect(() => { cartRef.current = cart; }, [cart]);
  useEffect(() => { currentWorkerRef.current = currentWorker; }, [currentWorker]);
  useEffect(() => { feesRef.current = fees; }, [fees]);

  // Tour bridge — translates demo steps into (auth + route + admin section) changes.
  const ensureView = useCallback(async (view: TourView) => {
    const gotoParent = async (studentId: string = EMMA_ID) => {
      if (userRef.current?.role !== 'parent') {
        try { logout(); } catch { /* noop */ }
        await login('lisa.johnson@email.com', mockPassword, 'parent');
      }
      // Always align the active student to what the step needs.
      if (studentRef.current?.id !== studentId) {
        selectStudent(studentId);
      }
    };
    const seedCart = () => {
      // Seed Lisa's cart with 3 representative items so steps 8 / 9 / 11-13
      // land on a populated cart and checkout. Resets first (clears any
      // residue from step 7's add-then-remove flow) and then adds the
      // demo items — guarantees the cart always has the same 3 items
      // when a 'cart-seeded' or 'checkout-seeded' view is requested.
      const chosen = [
        { id: '850e8400-e29b-41d4-a716-446655440008', qty: 2 }, // Spring Concert Ticket
        { id: '850e8400-e29b-41d4-a716-446655440010', qty: 1, partial: 0.5 }, // Yearbook — partial payment demo
        { id: '850e8400-e29b-41d4-a716-446655440003', qty: 1 }, // Art Supplies Kit
      ];
      const desiredFeeIds = new Set(chosen.map((c) => c.id));
      const have = cartRef.current.items;
      const haveFeeIds = new Set(have.map((it) => it.feeId));
      // If the cart already has exactly the seeded items (and nothing
      // else), skip — avoids re-seeding on every cart-seeded step.
      const exactMatch =
        have.length === chosen.length &&
        chosen.every((c) => haveFeeIds.has(c.id)) &&
        have.every((it) => desiredFeeIds.has(it.feeId));
      if (exactMatch) return;
      // Clear out any items that don't belong to the seed (e.g. the
      // activity pass that step 7 added but didn't remove cleanly).
      have.forEach((it) => {
        if (!desiredFeeIds.has(it.feeId)) removeFromCart(it.id);
      });
      // Add any seed items that aren't already present.
      for (const row of chosen) {
        if (haveFeeIds.has(row.id)) continue;
        const fee = feesRef.current.find((f) => f.id === row.id);
        if (!fee) continue;
        const tier = 'full' as const;
        const partialAmount = row.partial
          ? Math.round(fee.basePrice * row.qty * row.partial * 100) / 100
          : undefined;
        const partialPct = row.partial ? row.partial * 100 : undefined;
        addToCart(
          fee,
          row.qty,
          tier,
          undefined, // questionnaireResponses
          undefined, // signatureData
          partialAmount,
          undefined, // customUnitPrice
          partialPct,
        );
      }
    };
    const gotoCommunity = async () => {
      if (userRef.current?.role !== 'community') {
        try { logout(); } catch { /* noop */ }
        await login('james.anderson@email.com', mockPassword, 'community');
      }
    };
    const gotoAdmin = async (section: string) => {
      if (userRef.current?.role !== 'admin') {
        try { logout(); } catch { /* noop */ }
        await login('admin@school.edu', 'Admin123!', 'admin');
      }
      setCurrentRoute('admin-dashboard');
      // Defer the section event so AdminDashboard has a render tick to
      // mount its listener — otherwise the first admin-* step in the
      // tour dispatches before the listener is attached and the
      // section never switches (stuck on 'metrics').
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('cc-admin-set-section', { detail: section }));
      }, 60);
    };
    const gotoPosTerminal = () => {
      if (!currentWorkerRef.current) {
        const concessionWorker = mockWorkers.find((w) => w.isActive) ?? mockWorkers[0];
        if (concessionWorker) setCurrentWorker(concessionWorker);
      }
      setCurrentRoute('pos-terminal');
    };

    switch (view) {
      case 'login':
        try { logout(); } catch { /* noop */ }
        setCurrentRoute('login');
        return;
      case 'parent-dashboard':
        await gotoParent();
        setCurrentRoute('dashboard');
        return;
      case 'fee-catalog':
        await gotoParent(EMMA_ID);
        setCurrentRoute('catalog');
        return;
      case 'fee-catalog-reduced':
        await gotoParent(LIAM_ID);
        setCurrentRoute('catalog');
        return;
      case 'cart':
        await gotoParent();
        setCurrentRoute('cart');
        return;
      case 'cart-seeded':
        await gotoParent();
        seedCart();
        setCurrentRoute('cart');
        return;
      case 'checkout':
        await gotoParent();
        setCurrentRoute('checkout');
        return;
      case 'checkout-seeded':
        await gotoParent();
        seedCart();
        setCurrentRoute('checkout');
        return;
      case 'purchases':
        await gotoParent();
        setCurrentRoute('purchases');
        return;
      case 'events':
        setCurrentRoute('events');
        return;
      case 'community-shop':
        await gotoCommunity();
        setCurrentRoute('community');
        return;
      case 'admin-login':
        try { logout(); } catch { /* noop */ }
        setCurrentRoute('admin');
        return;
      case 'admin-dashboard':
        await gotoAdmin('metrics');
        return;
      case 'admin-fees':
        await gotoAdmin('fees');
        return;
      case 'admin-preassess':
        await gotoAdmin('feeassessment');
        return;
      case 'admin-orders':
        await gotoAdmin('orders');
        return;
      case 'admin-bulk-import':
        await gotoAdmin('bulkimport');
        return;
      case 'admin-notifications':
        await gotoAdmin('notifications');
        return;
      case 'pos-login':
        try { logout(); } catch { /* noop */ }
        setCurrentWorker(null);
        setCurrentRoute('pos');
        return;
      case 'pos-terminal':
        gotoPosTerminal();
        return;
      default:
        return;
    }
    // All refs above — ensureView stays stable. Only the truly stable
    // context callbacks (login/logout/selectStudent/addToCart) are in deps.
  }, [login, logout, selectStudent, addToCart, removeFromCart]);

  const tourBridge = useMemo<TourHostBridge>(() => ({ ensureView }), [ensureView]);

  const handleNavigate = (path: string) => {
    const routeMap: Record<string, Route> = {
      '/': 'login',
      '/register': 'register',
      '/register-community': 'register-community',
      '/dashboard': 'dashboard',
      '/catalog': 'catalog',
      '/cart': 'cart',
      '/checkout': 'checkout',
      '/share': 'share',
      '/community': 'community',
      '/events': 'events',
      '/purchases': 'purchases',
      '/account': 'account',
      '/admin': 'admin',
      '/admin-dashboard': 'admin-dashboard',
      '/pos': 'pos',
      '/pos-terminal': 'pos-terminal',
    };
    setCurrentRoute(routeMap[path] || 'login');
  };

  const handleWorkerLogin = (worker: Worker) => {
    setCurrentWorker(worker);
    setCurrentRoute('pos-terminal');
  };

  const handleWorkerLogout = () => {
    setCurrentWorker(null);
    setCurrentRoute('pos');
  };

  const handleLoginSuccess = () => {
    if (user?.role === 'community') {
      setCurrentRoute('community');
    } else if (user?.role === 'admin') {
      setCurrentRoute('admin-dashboard');
    } else {
      setCurrentRoute('dashboard');
    }
  };

  const handleAdminLoginSuccess = () => {
    setCurrentRoute('admin-dashboard');
  };

  const handleAdminLogout = () => {
    logout();
    setCurrentRoute('admin');
  };

  if (!isAuthenticated && currentRoute !== 'login' && currentRoute !== 'register' && currentRoute !== 'register-community' && currentRoute !== 'events' && currentRoute !== 'cart' && currentRoute !== 'checkout' && currentRoute !== 'admin' && currentRoute !== 'pos' && currentRoute !== 'pos-terminal') {
    if (currentRoute === 'admin-dashboard') {
      setCurrentRoute('admin');
    } else {
      setCurrentRoute('login');
    }
  }

  const renderContent = () => {
    if (currentRoute === 'pos') {
      return (
        <WorkerLoginPage
          onLoginSuccess={handleWorkerLogin}
          onBackToMain={() => handleNavigate('/')}
        />
      );
    }

    if (currentRoute === 'pos-terminal') {
      if (!currentWorker) {
        return (
          <WorkerLoginPage
            onLoginSuccess={handleWorkerLogin}
            onBackToMain={() => handleNavigate('/')}
          />
        );
      }
      return <POSInterface worker={currentWorker} onLogout={handleWorkerLogout} />;
    }

    if (currentRoute === 'admin') {
      if (!isAuthenticated || user?.role !== 'admin') {
        return <AdminLoginPage onLoginSuccess={handleAdminLoginSuccess} />;
      } else {
        return <AdminDashboard onLogout={handleAdminLogout} />;
      }
    }

    if (currentRoute === 'admin-dashboard') {
      if (!isAuthenticated || user?.role !== 'admin') {
        setCurrentRoute('admin');
        return <AdminLoginPage onLoginSuccess={handleAdminLoginSuccess} />;
      }
      return <AdminDashboard onLogout={handleAdminLogout} />;
    }

    if (!isAuthenticated && currentRoute !== 'register' && currentRoute !== 'register-community' && currentRoute !== 'events' && currentRoute !== 'cart' && currentRoute !== 'checkout') {
      return <LoginPage onLoginSuccess={handleLoginSuccess} onNavigate={handleNavigate} />;
    }

    switch (currentRoute) {
      case 'register':
        return (
          <ParentRegistration
            onRegistrationSuccess={handleLoginSuccess}
            onBackToLogin={() => handleNavigate('/')}
          />
        );
      case 'register-community':
        return (
          <CommunityRegistration
            onRegistrationSuccess={handleLoginSuccess}
            onBackToLogin={() => handleNavigate('/')}
          />
        );
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigate} />;
      case 'catalog':
        return <FeeCatalog />;
      case 'events':
        return <EventsPage />;
      case 'cart':
        return <CartPage onNavigate={handleNavigate} />;
      case 'checkout':
        return <CheckoutPage onNavigate={handleNavigate} />;
      case 'share':
        return <PublicCartAccess />;
      case 'community':
        return <CommunityShop />;
      case 'purchases':
        return <PurchasesPage onNavigate={handleNavigate} />;
      case 'account':
        return <AccountSettings />;
      default:
        return isAuthenticated ? <Dashboard onNavigate={handleNavigate} /> : <LoginPage onLoginSuccess={handleLoginSuccess} onNavigate={handleNavigate} />;
    }
  };

  const showAppBar = (isAuthenticated || currentRoute === 'events' || currentRoute === 'cart' || currentRoute === 'checkout') && currentRoute !== 'admin' && currentRoute !== 'admin-dashboard' && currentRoute !== 'pos' && currentRoute !== 'pos-terminal';
  const showFooter = currentRoute !== 'admin' && currentRoute !== 'admin-dashboard' && currentRoute !== 'pos' && currentRoute !== 'pos-terminal';

  // Hide the tour on mobile aspect ratios — the booth-sized callout
  // card and the cursor-flight choreography are designed for laptop /
  // big-monitor viewports. Below 900px we just let the real app
  // breathe. Live-reactive: if the viewport resizes through the
  // breakpoint, the tour mounts or unmounts accordingly.
  // Gate the entire TourProvider (not just its children) so the
  // autoplay interval and the sessionStorage auto-resume don't run
  // on mobile — otherwise a tour mid-flight on desktop would keep
  // ticking after a resize and silently re-route the mobile user.
  const isMobile = useMediaQuery('(max-width: 899px)');

  const shell = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <DemoBanner />
      {showAppBar && <AppBar onNavigate={handleNavigate} />}
      <Box sx={{ flex: 1 }}>
        {renderContent()}
      </Box>
      {showFooter && <Footer />}
      <StudentSelector />
      {!isMobile && <TourLauncher />}
      {!isMobile && <TourOverlay />}
    </Box>
  );

  if (isMobile) return shell;

  return (
    <TourProvider bridge={tourBridge}>
      {shell}
    </TourProvider>
  );
};

const ThemedApp: React.FC = () => {
  const { theme } = useSchoolSettings();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <DataProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </DataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SchoolSettingsProvider>
        <ThemedApp />
      </SchoolSettingsProvider>
    </QueryClientProvider>
  );
}

export default App;
